import { useEffect, useState } from 'react';
import type { MultiValue } from 'react-select';
import { Select } from '~/components/ui/Form';
import type { SelectOption, SelectProps } from '~/components/ui/forms/Select';
import Icon, { RemixIconNames } from '~/components/ui/Icon';
import { createTag, getTags, searchTags } from '~/tags.ts';
import { SkillTagCategory, TagCategory } from '~/types/domain';
import { TagResponse } from '~/types/platform.ts';
import type { DataTestAttributes } from '~/utils/qa';

type OptionType = {
    label: string;
    value: string;
} & DataTestAttributes;

export type TagSelectorProps = {
    value: MultiValue<OptionType | string | number>;
    disabled?: boolean;
    category?: TagCategory;
    onChange: (value: SelectOption<string>[] | number[]) => void;
    full?: boolean;
    creatable?: boolean;
} & Omit<SelectProps<string>, 'isMulti'> &
    DataTestAttributes;

export default function TagSelector({
    value,
    disabled = false,
    category = SkillTagCategory,
    onChange,
    full = false,
    creatable = true,
    ['data-test-component']: testComponent = 'admin/TagSelector',
    ['data-test-name']: testName,
    ...selectProps
}: TagSelectorProps) {
    const [values, setValues] = useState<SelectOption<string>[]>([]);

    const loadTags = (value: MultiValue<OptionType | string | number>) => {
        const ids = value.map((v) => (typeof v === 'object' ? Number(v.value) : Number(v)));

        getTags(ids)
            .then((tags) => {
                setValues(
                    ids
                        .filter((id) => tags.has(id))
                        .map(
                            (id) =>
                                ({
                                    label: tags.get(id),
                                    value: String(id),
                                    ['data-test-component']: 'admin/TagSelector/Option',
                                }) as OptionType,
                        ),
                );
            })
            .catch((err) => {
                console.warn(err);
                setValues([]);
            });
    };

    useEffect(() => {
        if (value) {
            loadTags(value);
        }
    }, [value]);

    const loadOptions = async (inputValue: string) => {
        const data: TagResponse[] = await searchTags(inputValue.trim(), category);
        return data.map((r) => ({
            label: r.name,
            value: String(r.id),
        }));
    };

    const handleChange = (v: SelectOption<string>[]) => {
        onChange(full ? v : v.map((v) => Number(v.value)));
        setValues(v);
    };

    const handleCreate = async (input: string) => {
        await createTag(input.trim(), category).then((tag) => {
            handleChange([...values, { value: String(tag.id!), label: tag.name! }]); // eslint-disable-line @typescript-eslint/no-unnecessary-type-assertion
        });
    };

    return (
        <Select
            cacheOptions
            isMulti
            creatable={creatable}
            isDisabled={disabled}
            placeholder='Search tags...'
            loadOptions={loadOptions}
            onCreateOption={(input: string) => {
                handleCreate(input).catch((err) => console.error(err));
            }}
            noOptionsMessage={() => null}
            formatCreateLabel={(input: string) => (
                <div
                    className='admin-tag-selector-create-option'
                    data-test-component='admin/TagSelector/CreateTag'
                    data-test-value={input}>
                    <div>Create new tag "{input}"</div>
                    <div className='admin-tag-selector-create-option-warning'>
                        <Icon
                            icon={RemixIconNames.RiAlertFill}
                            size={18}
                        />
                        <span style={{ lineBreak: 'anywhere' }}>
                            Only create a new tag if you first tried to search for a tag and you are
                            completely sure the tag does not exist.
                        </span>
                    </div>
                </div>
            )}
            value={values}
            isClearable
            onChange={handleChange}
            data-test-component={testComponent}
            data-test-name={testName}
            {...selectProps}
        />
    );
}
